import React, { useState, useEffect, useCallback } from "react";
import "./styles.css";

// ==================== SLEEPER API ====================
const SLEEPER_BASE = "https://api.sleeper.app/v1";

const SleeperAPI = {
  async getUser(username) {
    const res = await fetch(`${SLEEPER_BASE}/user/${username}`);
    if (!res.ok) throw new Error("User not found");
    return res.json();
  },
  async getLeague(leagueId) {
    const res = await fetch(`${SLEEPER_BASE}/league/${leagueId}`);
    if (!res.ok) throw new Error("League not found");
    return res.json();
  },
  async getRosters(leagueId) {
    const res = await fetch(`${SLEEPER_BASE}/league/${leagueId}/rosters`);
    if (!res.ok) throw new Error("Could not load rosters");
    return res.json();
  },
  async getUsers(leagueId) {
    const res = await fetch(`${SLEEPER_BASE}/league/${leagueId}/users`);
    if (!res.ok) throw new Error("Could not load users");
    return res.json();
  },
  async getMatchups(leagueId, week) {
    const res = await fetch(`${SLEEPER_BASE}/league/${leagueId}/matchups/${week}`);
    if (!res.ok) throw new Error("Could not load matchups");
    return res.json();
  },
  async getDrafts(leagueId) {
    const res = await fetch(`${SLEEPER_BASE}/league/${leagueId}/drafts`);
    if (!res.ok) return [];
    return res.json();
  },
  async getDraftPicks(draftId) {
    const res = await fetch(`${SLEEPER_BASE}/draft/${draftId}/picks`);
    if (!res.ok) return [];
    return res.json();
  },
  async getTransactions(leagueId, week) {
    const res = await fetch(`${SLEEPER_BASE}/league/${leagueId}/transactions/${week}`);
    if (!res.ok) return [];
    return res.json();
  },
  async getNflState() {
    const res = await fetch(`${SLEEPER_BASE}/state/nfl`);
    if (!res.ok) throw new Error("Could not get NFL state");
    return res.json();
  },
  async getPlayoffBracket(leagueId) {
    const res = await fetch(`${SLEEPER_BASE}/league/${leagueId}/winners_bracket`);
    if (!res.ok) return [];
    return res.json();
  }
};

// ==================== DEMO DATA ====================
const DEMO_USERS = [
  { user_id: "1", display_name: "Mike D.", metadata: { team_name: "Tusked Terrors" }, avatar: null },
  { user_id: "2", display_name: "Sarah K.", metadata: { team_name: "Warthog Wreckers" }, avatar: null },
  { user_id: "3", display_name: "Dave R.", metadata: { team_name: "Snout Stampede" }, avatar: null },
  { user_id: "4", display_name: "Tom B.", metadata: { team_name: "Gridiron Grunters" }, avatar: null },
  { user_id: "5", display_name: "Jenny L.", metadata: { team_name: "Mud Bowl Maulers" }, avatar: null },
  { user_id: "6", display_name: "Chris W.", metadata: { team_name: "Tusk Dynasty" }, avatar: null },
  { user_id: "7", display_name: "Pat M.", metadata: { team_name: "Hog Wild" }, avatar: null },
  { user_id: "8", display_name: "Alex T.", metadata: { team_name: "Pigpen Raiders" }, avatar: null },
  { user_id: "9", display_name: "Kim H.", metadata: { team_name: "Trotter Troupe" }, avatar: null },
  { user_id: "10", display_name: "Rob N.", metadata: { team_name: "Slop Bucket FC" }, avatar: null },
];

const DEMO_ROSTERS = [
  { roster_id: 1, owner_id: "1", settings: { wins: 9, losses: 2, ties: 0, fpts: 1487, fpts_decimal: 30, fpts_against: 1198, fpts_against_decimal: 60 } },
  { roster_id: 2, owner_id: "2", settings: { wins: 8, losses: 3, ties: 0, fpts: 1456, fpts_decimal: 10, fpts_against: 1245, fpts_against_decimal: 20 } },
  { roster_id: 3, owner_id: "3", settings: { wins: 8, losses: 3, ties: 0, fpts: 1412, fpts_decimal: 80, fpts_against: 1268, fpts_against_decimal: 50 } },
  { roster_id: 4, owner_id: "4", settings: { wins: 7, losses: 4, ties: 0, fpts: 1389, fpts_decimal: 40, fpts_against: 1301, fpts_against_decimal: 70 } },
  { roster_id: 5, owner_id: "5", settings: { wins: 6, losses: 5, ties: 0, fpts: 1345, fpts_decimal: 90, fpts_against: 1322, fpts_against_decimal: 0 } },
  { roster_id: 6, owner_id: "6", settings: { wins: 5, losses: 6, ties: 0, fpts: 1298, fpts_decimal: 20, fpts_against: 1340, fpts_against_decimal: 40 } },
  { roster_id: 7, owner_id: "7", settings: { wins: 5, losses: 6, ties: 0, fpts: 1278, fpts_decimal: 60, fpts_against: 1356, fpts_against_decimal: 10 } },
  { roster_id: 8, owner_id: "8", settings: { wins: 4, losses: 7, ties: 0, fpts: 1234, fpts_decimal: 50, fpts_against: 1378, fpts_against_decimal: 90 } },
  { roster_id: 9, owner_id: "9", settings: { wins: 3, losses: 8, ties: 0, fpts: 1189, fpts_decimal: 30, fpts_against: 1402, fpts_against_decimal: 30 } },
  { roster_id: 10, owner_id: "10", settings: { wins: 1, losses: 10, ties: 0, fpts: 1023, fpts_decimal: 70, fpts_against: 1501, fpts_against_decimal: 20 } },
];

const DEMO_MATCHUPS = [
  { roster_id: 1, matchup_id: 1, points: 142.6 }, { roster_id: 2, matchup_id: 1, points: 138.1 },
  { roster_id: 3, matchup_id: 2, points: 131.4 }, { roster_id: 4, matchup_id: 2, points: 128.9 },
  { roster_id: 5, matchup_id: 3, points: 124.7 }, { roster_id: 6, matchup_id: 3, points: 119.3 },
  { roster_id: 7, matchup_id: 4, points: 117.2 }, { roster_id: 8, matchup_id: 4, points: 112.8 },
  { roster_id: 9, matchup_id: 5, points: 105.3 }, { roster_id: 10, matchup_id: 5, points: 96.1 },
];

const DEMO_DRAFT_PICKS = [
  { round: 1, pick_no: 1, roster_id: 1, metadata: { first_name: "Ja'Marr", last_name: "Chase", position: "WR", team: "CIN" }},
  { round: 1, pick_no: 2, roster_id: 2, metadata: { first_name: "CeeDee", last_name: "Lamb", position: "WR", team: "DAL" }},
  { round: 1, pick_no: 3, roster_id: 3, metadata: { first_name: "Saquon", last_name: "Barkley", position: "RB", team: "PHI" }},
  { round: 1, pick_no: 4, roster_id: 4, metadata: { first_name: "Tyreek", last_name: "Hill", position: "WR", team: "MIA" }},
  { round: 1, pick_no: 5, roster_id: 5, metadata: { first_name: "Bijan", last_name: "Robinson", position: "RB", team: "ATL" }},
  { round: 1, pick_no: 6, roster_id: 6, metadata: { first_name: "Amon-Ra", last_name: "St. Brown", position: "WR", team: "DET" }},
  { round: 1, pick_no: 7, roster_id: 7, metadata: { first_name: "Jahmyr", last_name: "Gibbs", position: "RB", team: "DET" }},
  { round: 1, pick_no: 8, roster_id: 8, metadata: { first_name: "Breece", last_name: "Hall", position: "RB", team: "NYJ" }},
  { round: 1, pick_no: 9, roster_id: 9, metadata: { first_name: "Derrick", last_name: "Henry", position: "RB", team: "BAL" }},
  { round: 1, pick_no: 10, roster_id: 10, metadata: { first_name: "A.J.", last_name: "Brown", position: "WR", team: "PHI" }},
  { round: 2, pick_no: 11, roster_id: 10, metadata: { first_name: "Lamar", last_name: "Jackson", position: "QB", team: "BAL" }},
  { round: 2, pick_no: 12, roster_id: 9, metadata: { first_name: "Josh", last_name: "Allen", position: "QB", team: "BUF" }},
  { round: 2, pick_no: 13, roster_id: 8, metadata: { first_name: "Patrick", last_name: "Mahomes", position: "QB", team: "KC" }},
  { round: 2, pick_no: 14, roster_id: 7, metadata: { first_name: "Travis", last_name: "Kelce", position: "TE", team: "KC" }},
  { round: 2, pick_no: 15, roster_id: 6, metadata: { first_name: "Sam", last_name: "LaPorta", position: "TE", team: "DET" }},
  { round: 2, pick_no: 16, roster_id: 5, metadata: { first_name: "Jalen", last_name: "Hurts", position: "QB", team: "PHI" }},
  { round: 2, pick_no: 17, roster_id: 4, metadata: { first_name: "Joe", last_name: "Burrow", position: "QB", team: "CIN" }},
  { round: 2, pick_no: 18, roster_id: 3, metadata: { first_name: "George", last_name: "Kittle", position: "TE", team: "SF" }},
  { round: 2, pick_no: 19, roster_id: 2, metadata: { first_name: "Mark", last_name: "Andrews", position: "TE", team: "BAL" }},
  { round: 2, pick_no: 20, roster_id: 1, metadata: { first_name: "Davante", last_name: "Adams", position: "WR", team: "LV" }},
];

const DEMO_NEWS = [
  { id: 1, date: "Nov 18, 2025", title: "MUD BOWL SET: #1 VS #2 CLASH THIS WEEK", body: "The league's two best teams finally face off in what promises to be the game of the year. The Tusked Terrors bring a 5-game win streak against the surging Warthog Wreckers. Bragging rights and the regular season title are on the line.", author: "Commissioner", type: "headline" },
  { id: 2, date: "Nov 15, 2025", title: "TRADE ALERT: Gridiron Grunters Ship RB1 for Draft Picks", body: "In a move that screams 'rebuilding,' Tom B. traded away his star running back for two future first-round picks. League sources say multiple teams inquired, but the Grunters liked the haul from Pigpen Raiders. Bold strategy, let's see if it pays off.", author: "Commissioner", type: "trade" },
  { id: 3, date: "Nov 11, 2025", title: "COMMISSIONER'S FINE: Bench-Stashing Kickers Is Now a Finable Offense", body: "After a heated group chat debate, the commissioner has ruled that rostering more than 2 kickers at any time will result in a $5 fine to the league pot. Yes, this rule was needed. You know who you are.", author: "Commissioner", type: "ruling" },
  { id: 4, date: "Nov 8, 2025", title: "Slop Bucket FC Watch: Can They Avoid 0-11?", body: "It's not looking good for Rob N.'s squad. With the league's worst scoring and a schedule that doesn't get any easier, the Toilet Bowl trophy is being polished as we speak. On the bright side, that #1 overall pick is looking nice.", author: "Commissioner", type: "roast" },
];

const DEMO_POWER_RANKINGS = [
  { rank: 1, rosterId: 1, blurb: "Five straight wins and the highest scoring team in the league. Nobody wants to face this squad in the playoffs." },
  { rank: 2, rosterId: 2, blurb: "The only team that can match the Terrors blow for blow. This week's matchup is a championship preview." },
  { rank: 3, rosterId: 3, blurb: "Quietly dominant. Same record as #2 but the point differential isn't as impressive. Still a legit contender." },
  { rank: 4, rosterId: 4, blurb: "That trade is going to either look genius or catastrophic in about 3 weeks. Riding the edge." },
  { rank: 5, rosterId: 5, blurb: "The most fun team to watch. Boom-or-bust lineup that either drops 160 or 95 with nothing in between." },
  { rank: 6, rosterId: 7, blurb: "Sneaky. Don't sleep on this team if they make the playoffs. Their floor has been rising." },
  { rank: 7, rosterId: 6, blurb: "Three straight losses have this team spiraling. Need to right the ship or they're watching playoffs from the couch." },
  { rank: 8, rosterId: 8, blurb: "That new RB acquisition could change everything. Or nothing. We'll see." },
  { rank: 9, rosterId: 9, blurb: "Mathematically alive but emotionally checked out. The vibes are off." },
  { rank: 10, rosterId: 10, blurb: "At this point we're just playing for pride. And the #1 draft pick. Mostly the draft pick." },
];

const DEMO_AWARDS = [
  { week: 11, boom: { rosterId: 1, points: 168.4, label: "BOOM 💥" }, bust: { rosterId: 6, points: 72.3, label: "BUST 💀" }, lucky: { rosterId: 8, points: 98.2, wonBy: 1.3, label: "LUCKY WIN 🍀" }, unlucky: { rosterId: 7, points: 134.8, lostBy: 0.7, label: "TOUGH LOSS 😤" }},
  { week: 10, boom: { rosterId: 2, points: 162.1, label: "BOOM 💥" }, bust: { rosterId: 10, points: 68.9, label: "BUST 💀" }, lucky: { rosterId: 5, points: 101.4, wonBy: 2.1, label: "LUCKY WIN 🍀" }, unlucky: { rosterId: 9, points: 128.6, lostBy: 0.4, label: "TOUGH LOSS 😤" }},
  { week: 9, boom: { rosterId: 3, points: 155.7, label: "BOOM 💥" }, bust: { rosterId: 9, points: 74.1, label: "BUST 💀" }, lucky: { rosterId: 6, points: 104.2, wonBy: 3.8, label: "LUCKY WIN 🍀" }, unlucky: { rosterId: 4, points: 141.2, lostBy: 1.1, label: "TOUGH LOSS 😤" }},
];

const DEMO_HOF = [
  { season: "2024-25", champion: "Tusked Terrors", owner: "Mike D.", record: "12-3", mvp: "Josh Allen" },
  { season: "2023-24", champion: "Snout Stampede", owner: "Dave R.", record: "11-4", mvp: "Tyreek Hill" },
  { season: "2022-23", champion: "Warthog Wreckers", owner: "Sarah K.", record: "13-2", mvp: "Justin Jefferson" },
  { season: "2021-22", champion: "Mud Bowl Maulers", owner: "Jenny L.", record: "10-5", mvp: "Cooper Kupp" },
  { season: "2020-21", champion: "Gridiron Grunters", owner: "Tom B.", record: "11-4", mvp: "Derrick Henry" },
  { season: "2019-20", champion: "Tusked Terrors", owner: "Mike D.", record: "12-3", mvp: "Christian McCaffrey" },
];

// ==================== HELPERS ====================

// Add Sleeper username → real name mappings here.
// The key is the Sleeper username (case-insensitive match), the value is the display name.
// Add more entries as needed!
const OWNER_NAME_MAP = {
  "wazucp": "Adam Cross",
  "rynospeed": "Ryan LaFrance",
};

function getRealName(sleeperUsername) {
  if (!sleeperUsername) return null;
  return OWNER_NAME_MAP[sleeperUsername.toLowerCase()] || null;
}

function getTeamName(users, rosters, rosterId) {
  const roster = rosters.find(r => r.roster_id === rosterId);
  if (!roster) return "Unknown";
  const user = users.find(u => u.user_id === roster.owner_id);
  if (!user) return "Unknown";
  return user.metadata?.team_name || user.display_name;
}

function getOwnerName(users, rosters, rosterId) {
  const roster = rosters.find(r => r.roster_id === rosterId);
  if (!roster) return "";
  const user = users.find(u => u.user_id === roster.owner_id);
  if (!user) return "";
  // Check the name map against both username and display_name
  return getRealName(user.username) || getRealName(user.display_name) || user.display_name || "";
}

function SectionHeader({ title, badge }) {
  return (
    <div className="ewff-section-header">
      <h2>{title}</h2>
      {badge && <span className="badge">{badge}</span>}
    </div>
  );
}

// ==================== STANDINGS ====================
function StandingsTab({ rosters, users, nflState, league }) {
  // Get division names from league metadata
  const divisionNames = {};
  if (league?.settings) {
    // Sleeper stores division names as metadata.division_1, division_2, etc.
    // or in settings as division_1_name, etc.
    for (let i = 1; i <= 4; i++) {
      const name = league.metadata?.[`division_${i}`] || league.settings?.[`division_${i}`] || null;
      if (name) divisionNames[i] = name;
    }
  }

  // Group rosters by division
  const hasDivisions = rosters.some(r => r.settings?.division && r.settings.division > 0);

  const sortRosters = (list) => [...list].sort((a, b) => {
    const wDiff = (b.settings?.wins || 0) - (a.settings?.wins || 0);
    if (wDiff !== 0) return wDiff;
    const aPF = (a.settings?.fpts || 0) + (a.settings?.fpts_decimal || 0) / 100;
    const bPF = (b.settings?.fpts || 0) + (b.settings?.fpts_decimal || 0) / 100;
    return bPF - aPF;
  });

  const renderTable = (rosterList, divRank) => (
    <div className="ewff-table-wrap">
      <table>
        <thead>
          <tr><th>#</th><th>Team</th><th>Owner</th><th>Record</th><th>PF</th><th>PA</th></tr>
        </thead>
        <tbody>
          {rosterList.map((r, i) => {
            const user = users.find(u => u.user_id === r.owner_id);
            const teamName = user?.metadata?.team_name || user?.display_name || "Team";
            const ownerName = getOwnerName(users, rosters, r.roster_id);
            const pf = ((r.settings?.fpts || 0) + (r.settings?.fpts_decimal || 0) / 100).toFixed(1);
            const pa = ((r.settings?.fpts_against || 0) + (r.settings?.fpts_against_decimal || 0) / 100).toFixed(1);
            return (
              <tr key={r.roster_id}>
                <td className={`ewff-rank ${i < 3 ? "top" : ""}`}>{i + 1}</td>
                <td className="ewff-team-name">{teamName}</td>
                <td>{ownerName}</td>
                <td className="ewff-record">{r.settings?.wins || 0}-{r.settings?.losses || 0}{r.settings?.ties ? `-${r.settings.ties}` : ""}</td>
                <td>{pf}</td>
                <td>{pa}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  if (hasDivisions) {
    // Group by division
    const divisions = {};
    rosters.forEach(r => {
      const div = r.settings?.division || 0;
      if (!divisions[div]) divisions[div] = [];
      divisions[div].push(r);
    });

    return (
      <div className="ewff-fade-in">
        <SectionHeader title="League Standings" badge={nflState ? `Through Week ${Math.max(1, (nflState.week || 1) - 1)}` : "Current Season"} />
        {Object.entries(divisions).sort(([a], [b]) => Number(a) - Number(b)).map(([divId, divRosters]) => (
          <div key={divId} style={{ marginBottom: 24 }}>
            <div className="ewff-division-header">
              {divisionNames[divId] || `Division ${divId}`}
            </div>
            <div className="ewff-card ewff-card-accent">
              {renderTable(sortRosters(divRosters))}
            </div>
          </div>
        ))}

        {/* Overall standings */}
        <div style={{ marginTop: 10 }}>
          <div className="ewff-division-header">Overall Standings</div>
          <div className="ewff-card ewff-card-accent">
            {renderTable(sortRosters(rosters))}
          </div>
        </div>
      </div>
    );
  }

  // No divisions — show flat standings
  return (
    <div className="ewff-fade-in">
      <SectionHeader title="League Standings" badge={nflState ? `Through Week ${Math.max(1, (nflState.week || 1) - 1)}` : "Current Season"} />
      <div className="ewff-card ewff-card-accent">
        {renderTable(sortRosters(rosters))}
      </div>
    </div>
  );
}

// ==================== MATCHUPS ====================
function MatchupsTab({ matchups, rosters, users, nflState }) {
  const paired = {};
  matchups.forEach(m => {
    if (!paired[m.matchup_id]) paired[m.matchup_id] = [];
    paired[m.matchup_id].push(m);
  });

  return (
    <div className="ewff-fade-in">
      <SectionHeader title={`Week ${nflState?.week || "?"} Matchups`} badge="Scores" />
      <div className="ewff-matchups-grid">
        {Object.entries(paired).map(([mid, teams]) => (
          <div key={mid} className="ewff-matchup-card">
            <div className="ewff-matchup-header">Matchup {mid}</div>
            <div className="ewff-matchup-body">
              <div className="ewff-matchup-team">
                <div className="name">{getTeamName(users, rosters, teams[0]?.roster_id)}</div>
                <div className="pts">{teams[0]?.points?.toFixed(1) || "0.0"}</div>
                <div className="owner">{getOwnerName(users, rosters, teams[0]?.roster_id)}</div>
              </div>
              <div className="ewff-vs">VS</div>
              <div className="ewff-matchup-team">
                <div className="name">{teams[1] ? getTeamName(users, rosters, teams[1].roster_id) : "BYE"}</div>
                <div className="pts">{teams[1]?.points?.toFixed(1) || "—"}</div>
                <div className="owner">{teams[1] ? getOwnerName(users, rosters, teams[1].roster_id) : ""}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== NEWS ====================
function NewsTab({ news }) {
  return (
    <div className="ewff-fade-in">
      <SectionHeader title="League News" badge="Commissioner's Desk" />
      {news.map(item => (
        <div key={item.id} className="ewff-news-item">
          <div className="ewff-news-type">{item.type}</div>
          <div className="ewff-news-content">
            <div className="ewff-news-date">{item.date}</div>
            <div className="ewff-news-title">{item.title}</div>
            <div className="ewff-news-body">{item.body}</div>
            <div className="ewff-news-author">— {item.author}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ==================== POWER RANKINGS ====================
function PowerRankingsTab({ rankings, rosters, users }) {
  return (
    <div className="ewff-fade-in">
      <SectionHeader title="Power Rankings" badge="Commissioner's Weekly Rankings" />
      <div className="ewff-card">
        {rankings.map(pr => (
          <div key={pr.rank} className="ewff-power-row">
            <div className={`ewff-power-rank ${pr.rank <= 3 ? "t3" : ""}`}>{pr.rank}</div>
            <div className="ewff-power-info">
              <div className="ewff-power-name">
                {getTeamName(users, rosters, pr.rosterId)}
                <span style={{ fontWeight: 400, fontSize: 12, color: "#8B3A1A", marginLeft: 8 }}>
                  ({getOwnerName(users, rosters, pr.rosterId)})
                </span>
              </div>
              <div className="ewff-power-blurb">{pr.blurb}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== AWARDS ====================
function AwardsTab({ awards, rosters, users }) {
  return (
    <div className="ewff-fade-in">
      <SectionHeader title="Weekly Awards" badge="Boom · Bust · Lucky · Unlucky" />
      {awards.map(week => (
        <div key={week.week} className="ewff-awards-week">
          <div className="ewff-awards-header">Week {week.week}</div>
          <div className="ewff-awards-grid">
            {[week.boom, week.bust, week.lucky, week.unlucky].map((a, i) => (
              <div key={i} className="ewff-award-cell">
                <div className="ewff-award-label">{a.label}</div>
                <div className="ewff-award-team">{getTeamName(users, rosters, a.rosterId)}</div>
                <div className="ewff-award-detail">
                  {a.points ? `${a.points} pts` : ""}
                  {a.wonBy ? `Won by ${a.wonBy} pts` : ""}
                  {a.lostBy ? `Lost by ${a.lostBy} pts` : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ==================== DRAFT ====================
function DraftTab({ draftPicks, rosters, users }) {
  if (!draftPicks || draftPicks.length === 0) {
    return (
      <div className="ewff-fade-in">
        <SectionHeader title="Draft Recap" badge="Full Draft Board" />
        <div className="ewff-card" style={{ padding: 30, textAlign: "center", fontFamily: "'Courier Prime', monospace", color: "#8B3A1A" }}>
          No draft data available yet. Draft results will appear here once your league completes its draft on Sleeper.
        </div>
      </div>
    );
  }

  return (
    <div className="ewff-fade-in">
      <SectionHeader title="Draft Recap" badge="Full Draft Board" />
      <div className="ewff-card">
        <div className="ewff-table-wrap">
          <table>
            <thead>
              <tr><th>Rd</th><th>Pick</th><th>Team</th><th>Player</th><th>Pos</th><th>NFL</th></tr>
            </thead>
            <tbody>
              {draftPicks.map((pick, i) => (
                <tr key={i}>
                  <td style={{ fontFamily: "'Bungee', cursive", fontSize: 12, color: "#2D4A22" }}>{pick.round}</td>
                  <td style={{ fontFamily: "'Courier Prime', monospace", fontWeight: 700 }}>{pick.pick_no}</td>
                  <td className="ewff-team-name">{getTeamName(users, rosters, pick.roster_id)}</td>
                  <td style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 600 }}>
                    {pick.metadata?.first_name} {pick.metadata?.last_name}
                  </td>
                  <td>
                    <span className={`ewff-pos-badge ewff-pos-${pick.metadata?.position || "DEF"}`}>
                      {pick.metadata?.position || "—"}
                    </span>
                  </td>
                  <td>{pick.metadata?.team || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ==================== HALL OF FAME ====================
function HallOfFameTab({ hallOfFame }) {
  return (
    <div className="ewff-fade-in">
      <SectionHeader title="Hall of Fame" badge="League Champions" />
      <div className="ewff-hof-grid">
        {hallOfFame.map((entry, i) => (
          <div key={i} className="ewff-trophy">
            <div className="ewff-trophy-season">{entry.season}</div>
            <div style={{ fontSize: 36, margin: "6px 0" }}>🏆</div>
            <div className="ewff-trophy-team">{entry.champion}</div>
            <div className="ewff-trophy-detail">
              Owner: {entry.owner}<br />
              Record: {entry.record}<br />
              MVP: {entry.mvp}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== TRANSACTIONS ====================
function TransactionsTab({ leagueId, rosters, users, nflState }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadTransactions() {
      if (!leagueId) {
        setTransactions([]);
        setLoading(false);
        return;
      }
      try {
        const currentWeek = nflState?.week || 1;
        const allTx = [];
        // Fetch last 3 weeks of transactions
        for (let w = currentWeek; w >= Math.max(1, currentWeek - 2); w--) {
          const tx = await SleeperAPI.getTransactions(leagueId, w);
          allTx.push(...tx);
        }
        setTransactions(allTx.sort((a, b) => (b.created || 0) - (a.created || 0)).slice(0, 15));
      } catch {
        setTransactions([]);
      }
      setLoading(false);
    }
    loadTransactions();
  }, [leagueId, nflState]);

  if (loading) return <div className="ewff-loading">Loading transactions...</div>;

  return (
    <div className="ewff-fade-in">
      <SectionHeader title="Recent Transactions" badge="Trades · Waivers · Free Agents" />
      {transactions.length === 0 ? (
        <div className="ewff-card" style={{ padding: 30, textAlign: "center", fontFamily: "'Courier Prime', monospace", color: "#8B3A1A" }}>
          No recent transactions found.
        </div>
      ) : (
        transactions.map((tx, i) => (
          <div key={tx.transaction_id || i} className="ewff-news-item">
            <div className="ewff-news-type">{tx.type?.replace("_", " ") || "transaction"}</div>
            <div className="ewff-news-content">
              <div className="ewff-news-date">
                {tx.created ? new Date(tx.created).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
              </div>
              <div className="ewff-news-body" style={{ fontSize: 13 }}>
                {tx.type === "trade" && `Trade between roster ${tx.roster_ids?.join(" & ") || "unknown"}`}
                {tx.type === "waiver" && "Waiver claim processed"}
                {tx.type === "free_agent" && "Free agent pickup"}
                {tx.type !== "trade" && tx.type !== "waiver" && tx.type !== "free_agent" && (tx.type || "Transaction")}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ==================== SETUP ====================
function SetupPanel({ onConnect, onDemo, error, loading }) {
  const [leagueId, setLeagueId] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (leagueId.trim()) onConnect(leagueId.trim());
  };

  return (
    <div className="ewff-setup">
      <h3>🐗 Connect Your League</h3>
      <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: 13, color: "#2C1810", lineHeight: 1.6, marginBottom: 16, textAlign: "center" }}>
        Enter your Sleeper league ID to pull live standings, matchups, draft results, and more.
        Find it in your Sleeper app under <strong>League Settings</strong>, or in the URL when viewing your league on the web.
      </p>

      {error && <div className="ewff-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <label>Sleeper League ID</label>
        <input
          type="text"
          placeholder="e.g. 924039165361360896"
          value={leagueId}
          onChange={e => setLeagueId(e.target.value)}
          disabled={loading}
        />
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          <button type="submit" className="ewff-btn" disabled={loading || !leagueId.trim()}>
            {loading ? "Connecting..." : "Connect League"}
          </button>
          <button type="button" className="ewff-btn ewff-btn-secondary" onClick={onDemo} disabled={loading}>
            View Demo
          </button>
        </div>
      </form>

      <div style={{ textAlign: "center", marginTop: 16 }}>
        <span className="ewff-sleeper-badge">Powered by Sleeper API · No login required</span>
      </div>

      <div style={{ marginTop: 20, fontFamily: "'Courier Prime', monospace", fontSize: 11, color: "#8B3A1A", lineHeight: 1.8 }}>
        <strong>How to find your League ID:</strong><br />
        1. Open the Sleeper app or sleeper.app<br />
        2. Go to your league<br />
        3. Tap Settings → League ID is listed there<br />
        4. Or copy it from the URL: sleeper.app/leagues/<strong>THIS_NUMBER</strong>
      </div>
    </div>
  );
}

// ==================== MAIN APP ====================
export default function App() {
  const [tab, setTab] = useState("standings");
  const [mode, setMode] = useState("setup");
  const [leagueId, setLeagueId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [league, setLeague] = useState(null);
  const [rosters, setRosters] = useState([]);
  const [users, setUsers] = useState([]);
  const [matchups, setMatchups] = useState([]);
  const [draftPicks, setDraftPicks] = useState([]);
  const [nflState, setNflState] = useState(null);

  const [news] = useState(DEMO_NEWS);
  const [powerRankings] = useState(DEMO_POWER_RANKINGS);
  const [awards] = useState(DEMO_AWARDS);
  const [hallOfFame] = useState(DEMO_HOF);

  // Check for saved league ID on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem("ewff_league_id");
      if (saved) {
        loadLeagueData(saved);
      }
    } catch {
      // localStorage not available, that's fine
    }
  }, []);

  const loadLeagueData = useCallback(async (lid) => {
    setLoading(true);
    setError(null);
    try {
      const [leagueData, rostersData, usersData, nflStateData] = await Promise.all([
        SleeperAPI.getLeague(lid),
        SleeperAPI.getRosters(lid),
        SleeperAPI.getUsers(lid),
        SleeperAPI.getNflState(),
      ]);

      setLeague(leagueData);
      setRosters(rostersData);
      setUsers(usersData);
      setNflState(nflStateData);

      const currentWeek = nflStateData?.week || 1;
      const matchupsData = await SleeperAPI.getMatchups(lid, currentWeek);
      setMatchups(matchupsData);

      const drafts = await SleeperAPI.getDrafts(lid);
      if (drafts && drafts.length > 0) {
        const picks = await SleeperAPI.getDraftPicks(drafts[0].draft_id);
        setDraftPicks(picks);
      }

      setLeagueId(lid);
      setMode("live");

      // Save league ID
      try { localStorage.setItem("ewff_league_id", lid); } catch {}
    } catch (err) {
      setError("Could not load league data. Please double-check your League ID and try again. Make sure it's just the number, no extra characters.");
      console.error("Sleeper API error:", err);
    }
    setLoading(false);
  }, []);

  const startDemo = () => {
    setRosters(DEMO_ROSTERS);
    setUsers(DEMO_USERS);
    setMatchups(DEMO_MATCHUPS);
    setDraftPicks(DEMO_DRAFT_PICKS);
    setNflState({ week: 12, season: "2025" });
    setMode("demo");
    setError(null);
  };

  const disconnect = () => {
    setMode("setup");
    setLeagueId(null);
    setLeague(null);
    setError(null);
    try { localStorage.removeItem("ewff_league_id"); } catch {}
  };

  const refreshData = () => {
    if (leagueId) loadLeagueData(leagueId);
  };

  const TABS = [
    { id: "standings", label: "Standings" },
    { id: "matchups", label: "Matchups" },
    { id: "news", label: "News" },
    { id: "power", label: "Rankings" },
    { id: "awards", label: "Awards" },
    { id: "transactions", label: "Moves" },
    { id: "draft", label: "Draft" },
    { id: "hof", label: "Hall of Fame" },
  ];

  const tickerItems = [
    "Evil Warthog Fantasy Football — Fear the Tusk",
    league?.name || "Connect your Sleeper league to see live data",
    "Standings update automatically from Sleeper",
    "Commissioner writes the news, power rankings & awards",
    "Visit the Hall of Fame to see past champions",
  ];

  const sleeperUrl = leagueId ? `https://sleeper.app/leagues/${leagueId}` : "https://sleeper.app";

  return (
    <div>
      {/* MASTHEAD */}
      <header className="ewff-masthead">
        <div className="ewff-masthead-date">Est. 1998 • Season XXVIII</div>
        <div style={{ fontSize: 48, margin: "4px 0", filter: "drop-shadow(2px 2px 0 #8B3A1A)" }}>🐗</div>
        <h1>Evil Warthog</h1>
        <div className="ewff-masthead-sub">Fantasy Football League</div>
      </header>

      <div className="ewff-edition">
        {league ? league.name : "2025–2026 Season"} • {league ? `${league.total_rosters}-Team` : "10-Team PPR"} • Powered by Sleeper
      </div>

      {mode === "demo" && (
        <div className="ewff-demo-banner">
          ⚠ DEMO MODE — Sample data shown&nbsp;&nbsp;
          <button className="ewff-btn ewff-btn-small" onClick={() => setMode("setup")} style={{ fontSize: 10, padding: "3px 10px", marginLeft: 6 }}>
            Connect Real League
          </button>
        </div>
      )}

      {mode === "live" && (
        <div className="ewff-demo-banner" style={{ background: "#2D4A22", color: "#F5E6C8" }}>
          ✓ CONNECTED TO SLEEPER&nbsp;&nbsp;
          <button className="ewff-btn ewff-btn-small" onClick={refreshData} style={{ fontSize: 10, padding: "3px 10px", marginLeft: 6, background: "#D4A017", color: "#2C1810" }}>
            Refresh
          </button>
          <button className="ewff-btn ewff-btn-small" onClick={disconnect} style={{ fontSize: 10, padding: "3px 10px", marginLeft: 6 }}>
            Disconnect
          </button>
        </div>
      )}

      {/* NAV */}
      {mode !== "setup" && (
        <nav className="ewff-nav">
          {TABS.map(t => (
            <button key={t.id} className={tab === t.id ? "active" : ""} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </nav>
      )}

      {/* SLEEPER LINK */}
      {mode !== "setup" && (
        <div className="ewff-link-bar">
          <a href={sleeperUrl} target="_blank" rel="noopener noreferrer">
            Open in Sleeper → Set Lineups, Make Trades & More
          </a>
        </div>
      )}

      {/* TICKER */}
      {mode !== "setup" && (
        <div className="ewff-ticker-wrap">
          <div className="ewff-ticker">
            {[...tickerItems, ...tickerItems].map((item, i) => (
              <span key={i}>{item}</span>
            ))}
          </div>
        </div>
      )}

      {/* CONTENT */}
      <main className="ewff-container">
        {mode === "setup" && (
          <SetupPanel onConnect={loadLeagueData} onDemo={startDemo} error={error} loading={loading} />
        )}

        {mode !== "setup" && !loading && (
          <>
            {tab === "standings" && <StandingsTab rosters={rosters} users={users} nflState={nflState} league={league} />}
            {tab === "matchups" && <MatchupsTab matchups={matchups} rosters={rosters} users={users} nflState={nflState} />}
            {tab === "news" && <NewsTab news={news} />}
            {tab === "power" && <PowerRankingsTab rankings={powerRankings} rosters={rosters} users={users} />}
            {tab === "awards" && <AwardsTab awards={awards} rosters={rosters} users={users} />}
            {tab === "transactions" && <TransactionsTab leagueId={leagueId} rosters={rosters} users={users} nflState={nflState} />}
            {tab === "draft" && <DraftTab draftPicks={draftPicks} rosters={rosters} users={users} />}
            {tab === "hof" && <HallOfFameTab hallOfFame={hallOfFame} />}
          </>
        )}

        {loading && <div className="ewff-loading">🐗 Loading league data from Sleeper...</div>}
      </main>

      {/* FOOTER */}
      <footer className="ewff-footer">
        <div className="ewff-footer-logo">🐗 Evil Warthog</div>
        <p>
          Fantasy Football League • Est. 1998<br />
          "Fear the Tusk"<br /><br />
          Live data powered by Sleeper API
        </p>
      </footer>
    </div>
  );
}
